// A self-contained three.js viewer for the 3D preview overlay: renderer, camera,
// orbit controls, lights, and a render-on-demand loop. Feed it a scene group with
// setScene(); export it with exportGLB()/exportOBJ().

import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter.js';
import { STLExporter } from 'three/examples/jsm/exporters/STLExporter.js';
import { USDZExporter } from 'three/examples/jsm/exporters/USDZExporter.js';
import { EdgePass } from './edges';
import { extrudePrism } from './extrude';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { GLOW } from './lights3d';
import { onTextureLoaded } from './woods3d';
import {
  FLOOR_Z,
  nodeOf,
  type NodeRole,
  ROLE,
} from './sceneNodes';
import { keepsPart } from '../isolation';
import {
  groupToDxf3d,
  groupToObjMtl,
} from './exports3d';
import {
  type SecPart,
  type SecSrc,
  srcMatches,
} from '../section';
import type { PickVolume } from '../roomFeaturePieces';
import type {
  RayHit,
  RayHits,
} from '../viewPicks';
import {
  type Pt3,
  rayIntoPrism,
} from '../handles';
import { closedSurface } from '../meshTopology';
import {
  type CameraPose,
  dollyPose,
  focusPose,
  orbitAbout,
  poseAt,
  poseDistance,
  standardPose,
  type StandardView,
} from '../view3dNav';
import {
  sectionOutline,
  trimBox,
} from '../section3d';

/** One thing the 3D view shows: a drawing object and, when it is one of its
 *  features (its record's `part`, a pick volume), which — a section's source, so
 *  both views pick by one rule (viewPicks.ts). */
export type Pick3d = SecSrc;

const pickKey = (p: Pick3d): string => (p.part ? `${p.id}|${p.part.kind}|${p.part.id}` : p.id);
/** The data seams' name of a pick: its object, and its feature's id after a `|`. */
const seamOf = (p: Pick3d): string => (p.part ? `${p.id}|${p.part.id}` : p.id);

/** The selection box's and the hover box's colours: the plan's grip yellow and
 *  its hover blue (`HOVER_OUTLINE_COLOR`). */
/** How see-through X-ray leaves the rooms' shells. */
const XRAY_OPACITY = 0.18;
/** How long a camera move glides (ms): Fit, Zoom to selection, a standard view, a focus. */
const GLIDE_MS = 300;
/** The nodes an isolate of part of an object leaves alone: the groups (their
 *  meshes decide) and the lights' rigs (Lights on's). */
const ISOLATE_SKIPS: ReadonlySet<NodeRole> = new Set(['fileRig', 'group', 'light', 'viewRig']);
const SELECT_COLOR = 0xffd166;
const HOVER_COLOR = 0x5ab4ff;

/** The section's cut (AutoCAD's and SketchUp's section fill): flat, unlit. */
const SECTION_FILL = '#44484f';
/** How far a section plane's outline reaches past the model (mm). */
const PLANE_MARGIN = 200;

/** The studio's light by day, and with the lights on: low enough that the lights
 *  stand out, high enough that every shape still reads — the sky's and the
 *  ambient light's, the image-based light's, the key light (a cool moonlight at
 *  night, against the lights' warm white), the background. */
const DAY = { hemi: 0.35, ambient: 0.15, env: 1, key: 2.4, keyColor: '#fff2e0', background: '#eef1f4' };
const NIGHT = { hemi: 0.16, ambient: 0.1, env: 0.22, key: 0.35, keyColor: '#aebfdc', background: '#1a1e26' };
/** How long the 3D view stays unchanged before its lit shaders are pre-warmed. */
const PREWARM_IDLE_MS = 800;

export class Viewer3D {
  private renderer: THREE.WebGLRenderer;
  private scene = new THREE.Scene();
  private camera: THREE.PerspectiveCamera;
  private controls: OrbitControls;
  private dir: THREE.DirectionalLight;
  private hemi: THREE.HemisphereLight;
  private ambient: THREE.AmbientLight;
  private ground: THREE.Mesh;
  /** The ground by day, and with the lights on: a Lambert surface then, which an
   *  area light does not reach — it casts no shadow, so a strip would light the
   *  ground through the walls. */
  private groundDay: THREE.Material;
  private groundNight: THREE.Material;
  private grid: THREE.GridHelper;
  private content: THREE.Group | null = null;
  /** The faint SolidWorks-style edges, found on screen over `content` (`EdgePass`). */
  private edges: EdgePass;
  private raf = 0;
  private dirty = true;
  private framed = false;
  /** A camera move under way (Fit, Zoom to selection, a standard view, a
   *  double-click's focus): from, to, and when it began. */
  private glide: { from: CameraPose; to: CameraPose; t0: number } | null = null;
  /** A left drag orbiting about the selection: the pointer's last place and the pivot. */
  private pivotDrag: { x: number; y: number; pivot: Pt3 } | null = null;
  private canvas: HTMLCanvasElement;
  // Click-to-select: raycast a click (not an orbit drag) back to the object a
  // mesh's record names, and outline the selection with box helpers.
  private raycaster = new THREE.Raycaster();
  private pointerDown: { x: number; y: number; button: number } | null = null;
  private selected: readonly Pick3d[] = [];
  /** The object whose features take a click as if solid (its see-through panes
   *  and door leaves too), and its holes' pick volumes — the selected room's or
   *  drawn wall's. */
  private featureRoom: string | null = null;
  private volumes: readonly PickVolume[] = [];
  private selBoxes: THREE.Box3Helper[] = [];
  /** What a tool would add or take, shown in the scene, by slot (`setGhost`). */
  private ghosts = new Map<string, THREE.Group>();
  /** The objects Isolate shows only some parts of, by the parts' keys. */
  private isolated: ReadonlyMap<string, ReadonlySet<string>> = new Map();
  /** The nodes the isolate keeps out now (shown again when it lets them back). */
  private isolatedOut = new WeakSet<THREE.Object3D>();
  private hover: Pick3d | null = null;
  private hoverBox: THREE.Box3Helper | null = null;
  private sizeWatch: ResizeObserver | null = null;
  /** A wood's image arriving after the scene was built draws it again — its
   *  materials take a map, so their lit shaders change too. */
  private woodWatch = onTextureLoaded(() => { this.dirty = true; this.contentChanged(); });
  /** Notified of a LEFT click (not an orbit drag): what it takes is the UI's to
   *  say (`hitsAt`, viewPicks.ts). */
  onSelect: ((e: PointerEvent) => void) | null = null;
  /** A right click (a right press let go where it was — a right drag pans), for
   *  the object menu. */
  onContext: ((e: PointerEvent) => void) | null = null;
  /** Run at the start of every frame, before it is drawn — a handle's drag is
   *  applied there, at most once a frame. */
  onFrame: (() => void) | null = null;
  /** Run after a frame is drawn: the handle overlay paints over it. */
  onRender: (() => void) | null = null;
  /** Hide ceilings (the status bar's toggle): a view setting, never saved. */
  private ceilingsHidden = false;
  /** X-ray: what `xrayOf` takes (the rooms' shells, drawn walls) drawn see-through
   *  — and so click-through: a pick passes what it X-rayed (`stops`). */
  private xray = false;
  /** The meshes X-ray sees through, each with its own material (it wears a
   *  see-through copy meanwhile). */
  private xrayed = new WeakMap<THREE.Object3D, THREE.Material | THREE.Material[]>();
  private xrayOf: (() => (id: string, part: SecPart | undefined) => boolean) | null = null;
  /** Lights on (the status bar's toggle): the studio dimmed, the rooms' lights
   *  shining — a view setting, never saved. `lightsOn` is what was asked for,
   *  `lightsShown` what the view shows: the lit shaders (one program per material
   *  and light count) are compiled in the background first, never in a frame. */
  private lightsOn = false;
  private lightsShown = false;
  /** The light counts (litSignature) whose lit shaders are compiled; the
   *  background compile under way (its generation, 0 for none); the idle timer of
   *  the next pre-warm. */
  private litReady: string | null = null;
  private litWork = 0;
  private litGen = 0;
  private litBusy = false;
  private litDrawn = false;
  private prewarm: ReturnType<typeof setTimeout> | null = null;
  private disposed = false;
  /** Told when switching the lights on waits for its shaders (true) and when the
   *  lit view has drawn (false): the 3D view's loading screen. */
  onLightsBusy: ((busy: boolean) => void) | null = null;
  /** Meshes taken out of the view, freed once a frame has drawn what replaced
   *  them. Freed at once, the last material of its kind takes its shader program
   *  with it and the next frame compiles it again — on every frame of a handle's
   *  drag, which rebuilds its room. */
  private retired: THREE.Object3D[] = [];
  /** The section plane the view is clipped by (three's frame: kept where
   *  n · p + c ≥ 0), one object every clipped material shares; and its drawing-
   *  frame figures, null while there is none. */
  private clipPlane = new THREE.Plane();
  private clip: { n: Pt3; c: number } | null = null;
  /** The cut's fill: the back faces of each opaque closed solid, which only show
   *  where the section opens it — one flat material, sharing the meshes' geometry. */
  private caps = new THREE.Group();
  /** The meshes the caps fill: every piece of each closed opaque solid. */
  private capped = new Set<THREE.Object3D>();
  private capMat = new THREE.MeshBasicMaterial({ color: SECTION_FILL, side: THREE.BackSide, toneMapped: false, polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -4 });
  /** The section planes the tool draws: the one placed and the one the pointer previews. */
  private planes = new THREE.Group();
  private planeMats = {
    placed: { fill: planeFill(SELECT_COLOR), line: planeLine(SELECT_COLOR) },
    preview: { fill: planeFill(HOVER_COLOR), line: planeLine(HOVER_COLOR) },
  };

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.0;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.localClippingEnabled = true;
    this.capMat.clippingPlanes = [this.clipPlane];
    this.scene.background = new THREE.Color(DAY.background);
    this.edges = new EdgePass(this.renderer);

    // Image-based lighting from a studio room: gives every PBR surface soft
    // reflections and grounded shading (glass, glossy tile, metal read as real).
    const pmrem = new THREE.PMREMGenerator(this.renderer);
    this.scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;

    this.camera = new THREE.PerspectiveCamera(50, 1, 1, 500000);
    this.camera.position.set(3000, 3000, 3000);

    this.controls = new OrbitControls(this.camera, canvas);
    this.controls.enableDamping = true;
    // The wheel zooms toward what the cursor is on, and a middle drag pans, as on
    // the plan (right drags pan too; the left orbits).
    this.controls.zoomToCursor = true;
    this.controls.mouseButtons = { LEFT: THREE.MOUSE.ROTATE, MIDDLE: THREE.MOUSE.PAN, RIGHT: THREE.MOUSE.PAN };
    this.controls.addEventListener('change', () => { this.dirty = true; });
    // The user's own orbit, pan or zoom takes over from a camera move under way.
    this.controls.addEventListener('start', () => { this.glide = null; });

    // Track the press so a click (barely moved) selects, while a drag orbits.
    canvas.addEventListener('pointerdown', (e) => { this.pointerDown = { x: e.clientX, y: e.clientY, button: e.button }; });
    canvas.addEventListener('pointerup', (e) => this.onPointerUp(e));
    // With something selected, a left drag orbits about its middle (`orbitAbout`):
    // the controls' own turn (about their target) is held off for that drag.
    canvas.addEventListener('pointerdown', (e) => {
      const box = e.button === 0 ? this.selectionBox() : null;
      if (!box) return;
      const c = box.getCenter(new THREE.Vector3());
      this.pivotDrag = { x: e.clientX, y: e.clientY, pivot: { x: c.x, y: -c.z, z: c.y } };
      this.controls.enableRotate = false;
    });
    canvas.addEventListener('pointermove', (e) => {
      const d = this.pivotDrag;
      if (!d) return;
      const dx = e.clientX - d.x, dy = e.clientY - d.y, k = (2 * Math.PI * this.controls.rotateSpeed) / (this.canvas.clientHeight || 1);
      d.x = e.clientX;
      d.y = e.clientY;
      if (dx || dy) { this.glide = null; this.setPose(orbitAbout(this.pose(), d.pivot, -dx * k, dy * k)); }
    });
    const endPivot = (): void => { if (!this.pivotDrag) return; this.pivotDrag = null; this.controls.enableRotate = true; };
    canvas.addEventListener('pointerup', endPivot);
    canvas.addEventListener('pointercancel', endPivot);

    const hemi = new THREE.HemisphereLight(0xffffff, 0x6a7078, DAY.hemi);
    hemi.position.set(0, 5000, 0);
    this.hemi = hemi;
    this.dir = new THREE.DirectionalLight(DAY.keyColor, DAY.key); // warm key light
    this.dir.position.set(4000, 8000, 6000);
    this.dir.castShadow = true;
    this.dir.shadow.mapSize.set(2048, 2048);
    this.dir.shadow.bias = -0.0004;
    this.ambient = new THREE.AmbientLight(0xffffff, DAY.ambient);
    this.scene.add(hemi, this.dir, this.dir.target, this.ambient);

    this.groundDay = new THREE.MeshStandardMaterial({ color: '#d7dbe0', roughness: 0.95, metalness: 0 });
    this.groundNight = new THREE.MeshLambertMaterial({ color: '#c4c9d0' });
    this.ground = new THREE.Mesh(new THREE.PlaneGeometry(200000, 200000), this.groundDay);
    this.ground.rotation.x = -Math.PI / 2;
    this.ground.position.y = FLOOR_Z.ground;
    this.ground.receiveShadow = true;
    this.grid = new THREE.GridHelper(20000, 40, 0xb8bfc7, 0xcbd0d6);
    this.scene.add(this.ground, this.grid, this.caps, this.planes);
  }

  /** Replace the shown content. Frames the camera when `frame` is true, or the
   *  first time non-empty content appears (so live updates onto an empty scene
   *  still get a sensible view); otherwise keeps the camera steady. */
  setScene(group: THREE.Group, frame = true): void {
    if (this.content) { this.scene.remove(this.content); this.retired.push(this.content); }
    this.content = group;
    this.applyCeilings();
    this.applyXray();
    this.applyLights();
    this.scene.add(group);
    this.applySection();
    const box = new THREE.Box3().setFromObject(group);
    if (frame || (!this.framed && !box.isEmpty())) this.frame(group);
    else this.fitGround(box);
    // Re-fit the outlines to the rebuilt meshes.
    this.refit();
    this.dirty = true;
    this.contentChanged();
  }

  /** A LEFT click that barely moved is the UI's click (`onSelect`), a RIGHT one
   *  its object menu (`onContext`); a drag orbits or pans, and a middle click
   *  does nothing. */
  private onPointerUp(e: PointerEvent): void {
    const down = this.pointerDown;
    this.pointerDown = null;
    if (!down || Math.hypot(e.clientX - down.x, e.clientY - down.y) > 5) return; // an orbit or a pan, not a click
    if (down.button === 2 && this.onContext) this.onContext(e);
    if (down.button !== 0 || !this.onSelect) return;
    this.onSelect(e);
  }

  /** The visible surfaces under a client point, nearest first — what is hidden
   *  (itself or a group it is in: a rig while the lights are off) or cut away by
   *  the section is not there. */
  private meshHitsAt(clientX: number, clientY: number): THREE.Intersection[] {
    const rect = this.canvas.getBoundingClientRect();
    const ndc = new THREE.Vector2(
      ((clientX - rect.left) / (rect.width || 1)) * 2 - 1,
      -((clientY - rect.top) / (rect.height || 1)) * 2 + 1,
    );
    this.raycaster.setFromCamera(ndc, this.camera);
    if (!this.content) return [];
    // A line (a schematic connection) is 1 px wide: it takes a click within a few
    // pixels of it, sized where the camera looks.
    const perPx = (2 * this.camera.position.distanceTo(this.controls.target) * Math.tan((this.camera.fov * Math.PI) / 360)) / (rect.height || 1);
    this.raycaster.params.Line = { threshold: LINE_PICK_PX * perPx };
    return this.raycaster.intersectObject(this.content, true)
      .filter((h) => shown(h.object) && (!this.clip || this.clipPlane.distanceToPoint(h.point) >= 0));
  }

  /** Where a ray enters what the section keeps of a pick volume, or null. */
  private volumeHit(ray: { o: Pt3; d: Pt3 }, v: PickVolume): number | null {
    const t = rayIntoPrism(ray, v);
    if (t == null || !this.clip) return t;
    const { n, c } = this.clip;
    const along = (q: Pt3): number => n.x * q.x + n.y * q.y + n.z * q.z;
    const kept = (s: number): number => along({ x: ray.o.x + s * ray.d.x, y: ray.o.y + s * ray.d.y, z: ray.o.z + s * ray.d.z }) + c;
    if (kept(t) >= 0) return t;
    // Its near part is cut away: the ray may still enter the rest past the plane.
    const dn = along(ray.d);
    if (dn <= 0) return null;
    const tp = -(along(ray.o) + c) / dn;
    const o = { x: ray.o.x + tp * ray.d.x, y: ray.o.y + tp * ray.d.y, z: ray.o.z + tp * ray.d.z };
    const t2 = rayIntoPrism({ o, d: ray.d }, v);
    return t2 == null ? null : tp + t2;
  }

  /** Everything under a client point, as plain records (viewPicks.ts decides
   *  what a click takes and where a gesture lands from them): each surface or
   *  line of an object the ray meets, and each pick volume it enters, nearest
   *  first — on a solid the section has cut open, the cut itself (on the plane,
   *  square to it) —, and where it meets the floor. */
  hitsAt(clientX: number, clientY: number): RayHits {
    const hits: RayHit[] = [];
    const ray = this.raycaster.ray;
    for (const h of this.meshHitsAt(clientX, clientY)) {
      const node = nodeOf(h.object), src = pickOf(h.object);
      if (!node || !src) continue; // no object's (an underlay) — see through it
      if (!h.face) { hits.push({ distance: h.distance, point: toDrawing(h.point), src, look: 'line' }); continue; }
      const look = ROLE[node.role].opaque ? 'opaque' : 'clear';
      const n = h.face.normal.clone().applyMatrix3(new THREE.Matrix3().getNormalMatrix(h.object.matrixWorld)).normalize();
      const cut = this.clip && n.dot(ray.direction) > 0 && this.capped.has(h.object) ? ray.intersectPlane(this.clipPlane, new THREE.Vector3()) : null;
      if (cut) {
        const c = this.clipPlane.normal;
        hits.push({ distance: ray.origin.distanceTo(cut), point: toDrawing(cut), normal: { x: -c.x, y: c.z, z: -c.y }, src, look });
      } else hits.push({ distance: h.distance, point: toDrawing(h.point), normal: { x: n.x, y: -n.z, z: n.y }, src, look });
    }
    const { origin: o, direction: d } = ray;
    const r = { o: { x: o.x, y: -o.z, z: o.y }, d: { x: d.x, y: -d.z, z: d.y } };
    for (const v of this.volumes) {
      const t = this.volumeHit(r, v);
      if (t != null) hits.push({ distance: t, point: { x: r.o.x + r.d.x * t, y: r.o.y + r.d.y * t, z: r.o.z + r.d.z * t }, src: v.src, look: 'volume' });
    }
    hits.sort((a, b) => a.distance - b.distance);
    const g = ray.intersectPlane(new THREE.Plane(new THREE.Vector3(0, 1, 0), 0), new THREE.Vector3());
    return { hits, floor: g ? { point: toDrawing(g), distance: ray.origin.distanceTo(g) } : null, live: this.featureRoom };
  }

  /** Whether the section keeps a drawing point (no section: every point). */
  keeps(p: Pt3): boolean {
    return !this.clip || this.clipPlane.distanceToPoint(new THREE.Vector3(p.x, p.z, -p.y)) >= 0;
  }

  /** Whether a node shows a solid's surface for the section's fill: its role is
   *  opaque and X-ray does not see through it. */
  private stops(o: THREE.Object3D): boolean {
    const n = nodeOf(o);
    return !!n && ROLE[n.role].opaque && !this.xrayed.has(o);
  }

  /** Show what a tool would add or take, in the scene itself — walls in front
   *  of it hide it —, in `slot` (one per kind of preview: a click's, a drag's,
   *  Copy's): translucent prisms on plan rings (drawing mm), `z0` to `z1` high,
   *  each outlined. Null or none empties the slot. Never picked, never exported;
   *  the seam `data-ghost` lists the slots shown. */
  setGhost(slot: string, parts: ReadonlyArray<{ ring: Array<{ x: number; y: number }>; z0: number; z1: number; color: string; opacity: number }> | null): void {
    const old = this.ghosts.get(slot);
    if (old) {
      this.scene.remove(old);
      old.traverse((o) => {
        const m = o as THREE.Mesh;
        m.geometry?.dispose();
        (m.material as THREE.Material | undefined)?.dispose();
      });
      this.ghosts.delete(slot);
    }
    if (parts?.length) {
      const group = new THREE.Group();
      for (const g of parts) {
        const mat = new THREE.MeshBasicMaterial({ color: new THREE.Color(g.color), transparent: true, opacity: g.opacity, depthWrite: false, side: THREE.DoubleSide, toneMapped: false });
        const mesh = extrudePrism(g.ring, g.z0, g.z1, mat);
        if (!mesh) { mat.dispose(); continue; }
        mesh.renderOrder = 10;
        const edges = new THREE.LineSegments(new THREE.EdgesGeometry(mesh.geometry), new THREE.LineBasicMaterial({ color: new THREE.Color(g.color), toneMapped: false }));
        edges.position.copy(mesh.position); edges.quaternion.copy(mesh.quaternion); edges.scale.copy(mesh.scale);
        edges.renderOrder = 11;
        group.add(mesh, edges);
      }
      if (group.children.length) { this.ghosts.set(slot, group); this.scene.add(group); }
    }
    this.canvas.dataset.ghost = [...this.ghosts.keys()].sort().join(' '); // test seam
    this.dirty = true;
  }

  /** How many css px a millimetre makes on screen at `p` (the larger of the
   *  plan's two axes there); 0 behind the camera. */
  pxPerMmAt(p: Pt3): number {
    const o = this.project(p);
    if (!o) return 0;
    const len = (q: { x: number; y: number } | null): number => (q ? Math.hypot(q.x - o.x, q.y - o.y) / 100 : 0);
    return Math.max(len(this.project({ x: p.x + 100, y: p.y, z: p.z })), len(this.project({ x: p.x, y: p.y + 100, z: p.z })));
  }

  /** Glide to what the client rectangle `a`–`b` shows: `at` (where its middle
   *  lands, viewPicks.ts) to the view's middle, as much closer as the rectangle is
   *  smaller than the view; the angle kept. False for a rectangle of next to
   *  nothing, or none to go to. */
  zoomToRect(a: { x: number; y: number }, b: { x: number; y: number }, at: Pt3 | null): boolean {
    const rect = this.canvas.getBoundingClientRect();
    const w = Math.abs(a.x - b.x), h = Math.abs(a.y - b.y);
    if ((w < 4 && h < 4) || !at) return false;
    const p = this.pose();
    const k = Math.max(w / (rect.width || 1), h / (rect.height || 1));
    const d = { x: p.eye.x - p.target.x, y: p.eye.y - p.target.y, z: p.eye.z - p.target.z };
    const dl = Math.hypot(d.x, d.y, d.z) || 1;
    const far = Math.max(Math.hypot(p.eye.x - at.x, p.eye.y - at.y, p.eye.z - at.z) * k, 200);
    this.glideTo({ eye: { x: at.x + (d.x / dl) * far, y: at.y + (d.y / dl) * far, z: at.z + (d.z / dl) * far }, target: at });
    return true;
  }

  /** The ray under a client point, in drawing coordinates (plan x, y and the
   *  height z; `d` a unit direction). */
  rayAt(clientX: number, clientY: number): { o: { x: number; y: number; z: number }; d: { x: number; y: number; z: number } } {
    const rect = this.canvas.getBoundingClientRect();
    const ndc = new THREE.Vector2(
      ((clientX - rect.left) / (rect.width || 1)) * 2 - 1,
      -((clientY - rect.top) / (rect.height || 1)) * 2 + 1,
    );
    this.camera.updateMatrixWorld();
    this.raycaster.setFromCamera(ndc, this.camera);
    const { origin: o, direction: d } = this.raycaster.ray;
    return { o: { x: o.x, y: -o.z, z: o.y }, d: { x: d.x, y: -d.z, z: d.y } };
  }

  /** A drawing point (plan x, y; height z) on the canvas, in css px from its
   *  top-left corner; null behind the camera. */
  project(p: { x: number; y: number; z: number }): { x: number; y: number } | null {
    this.camera.updateMatrixWorld();
    const v = new THREE.Vector3(p.x, p.z, -p.y);
    if (v.clone().applyMatrix4(this.camera.matrixWorldInverse).z >= 0) return null;
    v.project(this.camera);
    return { x: ((v.x + 1) / 2) * this.canvas.clientWidth, y: ((1 - v.y) / 2) * this.canvas.clientHeight };
  }

  /** Where the camera stands, in drawing coordinates. */
  eye(): { x: number; y: number; z: number } {
    const c = this.camera.position;
    return { x: c.x, y: -c.z, z: c.y };
  }

  /** Draw the next frame (what the overlay shows changed). */
  redraw(): void { this.dirty = true; }

  /** Swap the meshes of the objects `ids` for `group`'s (a scene built with only
   *  them): what a handle's drag shows at once — the rest of the scene, the
   *  camera and the outlines stay. Each child of a scene is one object's — a
   *  mesh, a line (a schematic connection), a room's lifted group, a light rig —,
   *  its record says whose. */
  replaceObjects(ids: ReadonlySet<string>, group: THREE.Group): void {
    if (!this.content) return;
    const theirs = (o: THREE.Object3D): boolean => { const owner = nodeOf(o)?.owner; return owner != null && ids.has(owner); };
    for (const child of [...this.content.children]) if (theirs(child)) { this.content.remove(child); this.retired.push(child); }
    for (const child of [...group.children]) this.content.add(child);
    this.applyCeilings();
    this.applyXray();
    this.applyLights();
    this.applySection();
    this.refit();
    this.dirty = true;
    this.contentChanged();
  }

  /** Clip the view by a section plane (drawing coordinates: what has
   *  `n · p + c ≥ 0` stays), or show the model whole. Its cut is filled
   *  (`SECTION_FILL`) and read as one face by the edges. */
  setSection(clip: { n: Pt3; c: number } | null): void {
    const was = !!this.clip;
    this.clip = clip ? { n: { ...clip.n }, c: clip.c } : null;
    if (clip) this.clipPlane.set(new THREE.Vector3(clip.n.x, clip.n.z, -clip.n.y), clip.c);
    if (was !== !!clip) {
      this.applySection();
      this.edges.setSection(clip ? this.clipPlane : null);
      this.contentChanged();
    }
    this.refit();
    // Test seam: the plane the view is clipped by.
    const f = (v: number): string => String(Math.round(v * 1000) / 1000);
    this.canvas.dataset.section = clip ? `${f(clip.n.x)},${f(clip.n.y)},${f(clip.n.z)},${Math.round(clip.c)}` : '';
    this.dirty = true;
  }

  /** Every material shown clipped by the section (none without one), and the
   *  cut's fill rebuilt over the meshes shown now. */
  private applySection(): void {
    const planes = this.clip ? [this.clipPlane] : null;
    this.content?.traverse((o) => {
      const mats = (o as THREE.Mesh).material;
      for (const m of Array.isArray(mats) ? mats : mats ? [mats] : []) {
        if (m.clippingPlanes === planes || (!m.clippingPlanes && !planes)) continue;
        m.clippingPlanes = planes;
        m.clipShadows = true;
      }
    });
    // The caps share the content's geometry: nothing of theirs to free.
    this.caps.clear();
    this.capped = new Set();
    if (this.clip && this.content) {
      this.content.updateMatrixWorld(true);
      // A solid built as several meshes (its record's `solid`: a wall in one mesh
      // per finish, a shell closed by its seat) is filled where they close it together.
      const groups = new Map<object, THREE.Mesh[]>();
      this.content.traverse((o) => {
        const m = o as THREE.Mesh;
        if (!m.isMesh || !this.stops(m)) return;
        const key = nodeOf(m)?.solid ?? m, list = groups.get(key);
        if (list) list.push(m);
        else groups.set(key, [m]);
      });
      const cap = (g: THREE.BufferGeometry, src: THREE.Mesh): void => {
        const c = new THREE.Mesh(g, this.capMat);
        c.matrixAutoUpdate = false;
        c.matrix.copy(src.matrixWorld);
        c.userData.src = src;
        this.caps.add(c);
      };
      for (const pieces of groups.values()) {
        const seat = nodeOf(pieces[0])?.solid?.seat ?? null;
        if (!closedSolid(pieces, seat)) continue;
        for (const m of pieces) { cap(m.geometry, m); this.capped.add(m); }
        if (seat) cap(seat, pieces[0]);
      }
      this.syncCaps();
    }
    this.dirty = true;
  }

  /** Test seams, read off what is shown: of the content's materials, how many
   *  the section clips (`k/n`); how many of its meshes the cut can fill; of its
   *  opaque meshes, those it cannot (`triangles@id`, a solid that is not closed). */
  private sectionSeams(): void {
    const mats = new Set<THREE.Material>(), meshes = new Set<THREE.Object3D>(), open: string[] = [];
    this.content?.traverse((o) => {
      meshes.add(o);
      const m = (o as THREE.Mesh).material;
      for (const x of Array.isArray(m) ? m : m ? [m] : []) mats.add(x);
      const g = (o as THREE.Mesh).geometry;
      if (this.clip && (o as THREE.Mesh).isMesh && this.stops(o) && !this.capped.has(o)) open.push(`${(g.index?.count ?? g.getAttribute('position')?.count ?? 0) / 3}@${nodeOf(o)?.owner ?? ''}`);
    });
    const clipped = [...mats].filter((m) => m.clippingPlanes?.includes(this.clipPlane)).length;
    this.canvas.dataset.clipped = `${clipped}/${mats.size}`;
    this.canvas.dataset.caps = String(this.caps.children.filter((c) => meshes.has(c.userData.src as THREE.Object3D)).length);
    this.canvas.dataset.unfilled = open.join(' ');
  }

  /** A cap shows while its mesh does (Hide ceilings). */
  private syncCaps(): void {
    for (const cap of this.caps.children) {
      let o = cap.userData.src as THREE.Object3D | null, shown = true;
      while (o && shown) { shown = o.visible; o = o.parent; }
      cap.visible = shown;
    }
  }

  /** Draw section planes over the model — the tool's: the one placed, and the
   *  one the pointer previews —, each where it meets the model's extents. */
  setSectionPlanes(list: ReadonlyArray<{ p: Pt3; n: Pt3; preview: boolean }>): void {
    for (const c of [...this.planes.children]) {
      this.planes.remove(c);
      (c as THREE.Mesh).geometry.dispose(); // the materials are kept for the next
    }
    const b = this.content ? new THREE.Box3().setFromObject(this.content) : null;
    const box = b && !b.isEmpty() ? { min: { x: b.min.x, y: -b.max.z, z: b.min.y }, max: { x: b.max.x, y: -b.min.z, z: b.max.y } } : null;
    for (const s of list) {
      const pts = sectionOutline(s.p, s.n, box, PLANE_MARGIN).map((q) => new THREE.Vector3(q.x, q.z, -q.y));
      const fan: number[] = [];
      for (let i = 1; i + 1 < pts.length; i++) fan.push(0, i, i + 1);
      const mats = s.preview ? this.planeMats.preview : this.planeMats.placed;
      const fill = new THREE.Mesh(new THREE.BufferGeometry().setFromPoints(pts).setIndex(fan), mats.fill);
      const line = new THREE.LineLoop(new THREE.BufferGeometry().setFromPoints(pts), mats.line);
      fill.renderOrder = 997;
      line.renderOrder = 998;
      this.planes.add(fill, line);
    }
    // Test seam: the planes drawn.
    this.canvas.dataset.sectionPlanes = list.map((s) => (s.preview ? 'preview' : 'placed')).join(',');
    this.dirty = true;
  }

  /** Outline the selection, one box per target: an object's meshes, or — for one
   *  of its room's or drawn wall's features — that feature's (its pick volume for
   *  a hole). `featureRoom`: the room or drawn wall whose features take a click
   *  as if solid — its see-through panes and door leaves too —, with the pick
   *  volumes of its holes (`featureVolumes`, `wallFeatureVolumes`): the selected
   *  one's, as the plan picks a feature once its room or wall is selected. */
  setSelection(targets: readonly Pick3d[], featureRoom: string | null = null, volumes: readonly PickVolume[] = []): void {
    this.selected = [...targets];
    this.featureRoom = featureRoom;
    this.volumes = featureRoom ? volumes : [];
    this.refit();
  }

  /** Re-fit the selection's and the hover's boxes to what is shown now. */
  private refit(): void {
    for (const b of this.selBoxes) this.dropBox(b);
    this.selBoxes = [];
    for (const t of this.selected) {
      const b = this.boxOf(t, SELECT_COLOR);
      if (b) this.selBoxes.push(b);
    }
    this.canvas.dataset.selected = this.selected.map(seamOf).join(',');
    this.fitHover();
  }

  /** Outline what the pointer is over (the plan's hover blue), or nothing. The
   *  same pick again changes nothing (no render on every pointer move). */
  setHover(pick: Pick3d | null): void {
    if ((pick ? pickKey(pick) : '') === (this.hover ? pickKey(this.hover) : '')) return;
    this.hover = pick;
    this.fitHover();
  }

  private fitHover(): void {
    const pick = this.hover;
    if (this.hoverBox) this.dropBox(this.hoverBox);
    this.hoverBox = pick ? this.boxOf(pick, HOVER_COLOR) : null;
    this.canvas.dataset.hover = pick ? seamOf(pick) : '';
    this.dirty = true;
  }

  /** A box round the meshes showing `t` (an object's, or only its feature's) and
   *  the pick volumes that are it, drawn over everything; null when none is shown. */
  private boxOf(t: Pick3d, color: number): THREE.Box3Helper | null {
    if (!this.content) return null;
    const box = new THREE.Box3();
    const mine = (p: Pick3d): boolean => srcMatches(p, t);
    this.content.traverse((o) => {
      const m = o as THREE.Mesh, pick = pickOf(o);
      if (!(m.isMesh || (o as THREE.Line).isLine) || !m.visible || pick?.id !== t.id) return;
      if (mine(pick)) box.expandByObject(m);
    });
    for (const v of this.volumes) {
      if (!mine(v.src)) continue;
      for (const p of v.ring) for (const z of [v.z0, v.z1]) box.expandByPoint(new THREE.Vector3(p.x, z, -p.y));
    }
    if (box.isEmpty()) return null;
    // A section trims the box to what it keeps.
    if (this.clip) {
      const kept = trimBox(box, this.clipPlane.normal, this.clipPlane.constant);
      if (!kept) return null;
      box.min.set(kept.min.x, kept.min.y, kept.min.z);
      box.max.set(kept.max.x, kept.max.y, kept.max.z);
    }
    const helper = new THREE.Box3Helper(box, new THREE.Color(color));
    const mat = helper.material as THREE.LineBasicMaterial;
    mat.depthTest = false; mat.transparent = true;
    helper.renderOrder = 999;
    this.scene.add(helper);
    return helper;
  }

  private dropBox(b: THREE.Box3Helper): void {
    this.scene.remove(b);
    this.retired.push(b); // its line program stays for the box drawn in its place
  }

  /** Glide the camera toward (`factor` < 1) or away from what it looks at —
   *  the zoom buttons in 3D. A press during a glide goes on from where that one
   *  was going, so quick presses add up. */
  dolly(factor: number): void {
    this.glideTo(dollyPose(this.glide?.to ?? this.pose(), factor));
  }

  /** Resize with the canvas, whatever changes its size (a panel shown or hidden,
   *  the window) — the live view only. */
  watchSize(): void {
    if (this.sizeWatch || typeof ResizeObserver === 'undefined') return;
    this.sizeWatch = new ResizeObserver(() => this.resize());
    this.sizeWatch.observe(this.canvas);
  }

  /** What the selection's boxes enclose (the scene's axes); null when none shows. */
  private selectionBox(): THREE.Box3 | null {
    const box = new THREE.Box3();
    for (const b of this.selBoxes) box.union(b.box);
    return box.isEmpty() ? null : box;
  }

  /** What the selection's boxes enclose, in drawing coordinates (plan x, y and
   *  the height z) — where the move gizmo stands; null when none is shown. */
  selectionBounds(): { min: { x: number; y: number; z: number }; max: { x: number; y: number; z: number } } | null {
    const box = this.selectionBox();
    if (!box) return null;
    return { min: { x: box.min.x, y: -box.max.z, z: box.min.y }, max: { x: box.max.x, y: -box.min.z, z: box.max.y } };
  }

  /** Bring the selection to the middle of the view, filling it, seen from where
   *  the camera looks now — Zoom to selection. False when nothing selected shows. */
  frameSelection(): boolean {
    const box = new THREE.Box3();
    for (const b of this.selBoxes) box.union(b.box);
    if (box.isEmpty()) return false;
    const centre = box.getCenter(new THREE.Vector3());
    const radius = Math.max(box.getSize(new THREE.Vector3()).length() / 2, 50);
    const fov = (this.camera.fov * Math.PI) / 180;
    const fit = Math.min(fov, 2 * Math.atan(Math.tan(fov / 2) * this.camera.aspect));
    const dist = (radius / Math.sin(fit / 2)) * 1.1;
    const dir = this.camera.position.clone().sub(this.controls.target);
    if (dir.lengthSq() < 1e-9) dir.set(1, 0.8, 1);
    dir.normalize();
    const eye = centre.clone().addScaledVector(dir, dist);
    this.camera.near = Math.min(this.camera.near, Math.max(1, dist / 100));
    this.camera.updateProjectionMatrix();
    this.glideTo({ eye: { x: eye.x, y: -eye.z, z: eye.y }, target: { x: centre.x, y: -centre.z, z: centre.y } });
    return true;
  }

  /** Frame the camera on what is shown — Zoom fit in the 3D view, gliding there. */
  reframe(): void {
    if (!this.content) return;
    this.frame(this.content, true);
    this.dirty = true;
  }

  private frame(group: THREE.Group, glide = false): void {
    const box = new THREE.Box3().setFromObject(group);
    if (box.isEmpty()) return;
    const size = box.getSize(new THREE.Vector3());
    const centre = box.getCenter(new THREE.Vector3());
    const radius = Math.max(size.x, size.y, size.z) || 2000;
    const dist = radius * 2.2;
    this.camera.near = Math.max(1, radius / 100);
    this.camera.far = dist * 10 + radius * 10;
    this.camera.updateProjectionMatrix();
    const to: CameraPose = { eye: { x: centre.x + dist, y: -(centre.z + dist), z: centre.y + dist * 0.8 }, target: { x: centre.x, y: -centre.z, z: centre.y } };
    if (glide) this.glideTo(to);
    else { this.glide = null; this.setPose(to); }
    this.fitGround(box);
    this.framed = true;
  }

  /** Where the camera stands and what it orbits, in drawing coordinates. */
  pose(): CameraPose {
    const e = this.camera.position, t = this.controls.target;
    return { eye: { x: e.x, y: -e.z, z: e.y }, target: { x: t.x, y: -t.z, z: t.y } };
  }

  /** Put the camera at `p` at once. */
  private setPose(p: CameraPose): void {
    this.camera.position.set(p.eye.x, p.eye.z, -p.eye.y);
    this.controls.target.set(p.target.x, p.target.z, -p.target.y);
    this.controls.update();
    this.dirty = true;
  }

  /** Move the camera to `to` over `GLIDE_MS`, eased (`poseAt`): at once where the
   *  user asks for reduced motion. */
  glideTo(to: CameraPose): void {
    if (typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches) { this.glide = null; this.setPose(to); return; }
    this.glide = { from: this.pose(), to, t0: performance.now() };
    this.canvas.dataset.gliding = '1'; // the seam says so from the start, not from its first frame
    this.dirty = true;
  }

  /** A standard view of what the camera orbits, from where it is now (its
   *  distance kept): Top, Front, Back, Left, Right, Isometric. */
  viewFrom(view: StandardView): void {
    const p = this.pose();
    this.glideTo(standardPose(view, p.target, poseDistance(p)));
  }

  /** A double-click: the camera moves so it orbits `p` (where the click lands,
   *  viewPicks.ts), seen as before. False for none. */
  focusOn(p: Pt3 | null): boolean {
    if (!p) return false;
    this.glideTo(focusPose(this.pose(), p));
    return true;
  }

  /** Sit the ground/grid under the scene and aim the key light at it. */
  private fitGround(box: THREE.Box3): void {
    if (box.isEmpty()) return;
    const centre = box.getCenter(new THREE.Vector3());
    const r = Math.max(...box.getSize(new THREE.Vector3()).toArray()) || 2000;
    this.dir.position.set(centre.x + r, centre.y + r * 2.2, centre.z + r * 0.8);
    this.dir.target.position.copy(centre);
    this.dir.target.updateMatrixWorld();
    // Frame the shadow camera on the scene so shadows stay crisp at any size.
    const cam = this.dir.shadow.camera;
    const s = r * 1.4;
    cam.left = -s; cam.right = s; cam.top = s; cam.bottom = -s;
    cam.near = 1; cam.far = r * 8;
    cam.updateProjectionMatrix();
    this.ground.position.set(centre.x, box.min.y + FLOOR_Z.ground, centre.z);
    this.grid.position.set(centre.x, box.min.y + FLOOR_Z.grid, centre.z);
  }

  resize(): void {
    const w = this.canvas.clientWidth || 1, h = this.canvas.clientHeight || 1;
    this.renderer.setSize(w, h, false);
    const buf = this.renderer.getDrawingBufferSize(new THREE.Vector2());
    this.edges.setSize(buf.x, buf.y);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.dirty = true;
  }

  /** Render only when the camera moved (incl. damping) or the scene changed. */
  start(): void {
    if (this.raf) return;
    const loop = (): void => {
      // The lit frame drawn last time has been shown (a GPU still compiling at
      // its first draw holds this frame back): the loading screen goes.
      if (this.litDrawn) { this.litDrawn = false; if (this.litBusy && this.lightsShown === this.lightsOn) this.setLitBusy(false); }
      this.onFrame?.();
      const moved = this.controls.update();
      if (this.glide) {
        const g = this.glide, t = (performance.now() - g.t0) / GLIDE_MS;
        if (t >= 1) this.glide = null;
        this.setPose(poseAt(g.from, g.to, t));
      }
      if (moved || this.dirty) {
        if (this.dirty) this.sectionSeams();
        this.renderer.render(this.scene, this.camera);
        this.edges.render(this.content, this.camera, this.clip ? this.caps : null, (o) => !ROLE[nodeOf(o)?.role ?? 'solid'].edges || this.xrayed.has(o));
        this.dirty = false;
        this.freeRetired();
        if (this.litBusy && this.lightsShown === this.lightsOn) this.litDrawn = true;
        // Test seam: the newest shader program's id, which stays put while
        // nothing new is compiled.
        const progs = this.renderer.info.programs ?? [];
        this.canvas.dataset.programs = String(progs.reduce((m, p) => Math.max(m, p.id), 0));
        // Test seam: the camera (eye | target, mm) and whether it is gliding.
        const cp = this.pose(), r = (q: { x: number; y: number; z: number }): string => [q.x, q.y, q.z].map(Math.round).join(',');
        this.canvas.dataset.camera = `${r(cp.eye)}|${r(cp.target)}`;
        this.canvas.dataset.gliding = this.glide ? '1' : '';
        this.onRender?.();
      }
      this.raf = requestAnimationFrame(loop);
    };
    this.raf = requestAnimationFrame(loop);
  }

  stop(): void {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.freeRetired();
  }

  private freeRetired(): void {
    for (const o of this.retired) disposeGroup(o, this.xrayed);
    this.retired = [];
  }

  /** Show or hide every room's ceiling (Hide ceilings); the outlines follow. */
  setCeilingsHidden(hidden: boolean): void {
    if (hidden === this.ceilingsHidden) return;
    this.ceilingsHidden = hidden;
    this.applyCeilings();
    this.refit();
    this.dirty = true;
  }

  /** Show only these parts of these objects (Isolate in 3D on a room's bench, a
   *  wall's door: isolation.ts); an empty map shows every part. */
  setIsolatedParts(parts: ReadonlyMap<string, ReadonlySet<string>>): void {
    this.isolated = parts;
    this.applyCeilings();
    this.dirty = true;
  }

  /** What shows: a ceiling unless Hide ceilings, and of an object isolated in
   *  part only its kept parts — the lights' rigs and the groups left to their
   *  own passes. */
  private applyCeilings(): void {
    let out = 0;
    this.content?.traverse((o) => {
      const n = nodeOf(o);
      if (!n) return;
      const keys = n.owner != null && !ISOLATE_SKIPS.has(n.role) ? this.isolated.get(n.owner) : undefined;
      const isolatedOut = !!keys && !keepsPart(keys, n.part);
      if (isolatedOut && (o as THREE.Mesh).isMesh) out++;
      if (n.ceiling || keys || this.isolatedOut.has(o)) o.visible = !((n.ceiling && this.ceilingsHidden) || isolatedOut);
      if (isolatedOut) this.isolatedOut.add(o); else this.isolatedOut.delete(o);
    });
    this.canvas.dataset.isolatedOut = out ? String(out) : ''; // test seam: how many meshes Isolate keeps out
    this.syncCaps();
  }

  /** X-ray on or off: `of` gives, once a pass (a rebuild brings new objects),
   *  which object's meshes — by id, and the feature a mesh is — go see-through. */
  setXray(on: boolean, of: () => (id: string, part: SecPart | undefined) => boolean): void {
    this.xray = on;
    this.xrayOf = of;
    this.applyXray();
    this.dirty = true;
  }

  /** Each mesh X-ray takes wears a see-through copy of its material (its own kept
   *  in `xrayed`, put back when X-ray goes); it writes no depth, the edges leave
   *  it out and a click passes it (`stops`), so what stands behind shows whole. */
  private applyXray(): void {
    const of = this.xray ? this.xrayOf?.() : null;
    let n = 0;
    this.content?.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh) return;
      const pick = pickOf(m);
      const want = !!of && !!pick && of(pick.id, pick.part);
      if (want) n++;
      const orig = this.xrayed.get(m);
      if (want && !orig) {
        const see = (mat: THREE.Material): THREE.Material => {
          const c = mat.clone();
          c.transparent = true;
          c.opacity = Math.min(c.opacity ?? 1, XRAY_OPACITY);
          c.depthWrite = false;
          return c;
        };
        this.xrayed.set(m, m.material);
        m.material = Array.isArray(m.material) ? m.material.map(see) : see(m.material);
      } else if (!want && orig) {
        for (const x of Array.isArray(m.material) ? m.material : [m.material]) x.dispose();
        m.material = orig;
        this.xrayed.delete(m);
      }
    });
    this.canvas.dataset.xray = n ? String(n) : ''; // test seam: how many meshes it sees through
  }

  /** Switch the rooms' lights on or off (Lights on): on, the studio's light goes
   *  down — every shape still reads —, the background dark, the grid away, the
   *  view's rigs shine and the fixtures glow brighter. */
  setLightsOn(on: boolean): void {
    if (on === this.lightsOn) return;
    this.lightsOn = on;
    this.syncLights();
  }

  /** Whether switching the lights on waits for its shaders. */
  get lightsBusy(): boolean { return this.litBusy; }

  /** Show what was asked for when its shaders are ready; else keep the view
   *  unlit, say it is busy, and compile them in the background. */
  private syncLights(): void {
    const sig = this.litSignature();
    const ready = !this.lightsOn || !sig || this.litReady === sig;
    const show = this.lightsOn && ready;
    if (show !== this.lightsShown) { this.lightsShown = show; this.applyLights(); this.dirty = true; }
    if (!this.lightsOn) { this.setLitBusy(false); return; }
    if (ready) return;
    this.setLitBusy(true);
    if (!this.litWork) {
      // Two frames first: the loading screen paints before the work begins.
      const g = this.litWork = ++this.litGen;
      requestAnimationFrame(() => requestAnimationFrame(() => { if (this.litWork === g) this.compileLit(g); }));
    }
  }

  private setLitBusy(busy: boolean): void {
    if (busy === this.litBusy) return;
    this.litBusy = busy;
    this.onLightsBusy?.(busy);
  }

  /** What the lit shaders depend on: how many lights of each kind the view's rigs
   *  hold, and whether a section clips the view — '' with no light at all, whose
   *  look needs no new shader worth waiting for. */
  private litSignature(): string {
    const n: Record<string, number> = {};
    let any = false;
    this.content?.traverse((o) => {
      if (nodeOf(o)?.role !== 'viewRig') return;
      for (const l of o.children) if ((l as THREE.Light).isLight) { n[l.type] = (n[l.type] ?? 0) + 1; any = true; }
    });
    if (!any) return '';
    return `${Object.keys(n).sort().map((k) => `${k}:${n[k]}`).join(',')}${this.clip ? '|clip' : ''}`;
  }

  /** The content or the section changed: lit shaders compiled for the old
   *  materials may have gone with them (unless the lit view kept them in use), so
   *  they are compiled again — now if the lights are wanted, else once the view
   *  has been idle a moment. */
  private contentChanged(): void {
    // Test seams: the objects the scene shows (their ids, sorted), and each line
    // drawn (a schematic connection), its id and pieces — after a whole build or
    // a partial one alike.
    const ids = new Set<string>(), lines: string[] = [];
    this.content?.traverse((o) => {
      const id = nodeOf(o)?.owner;
      if (id) ids.add(id);
      if ((o as THREE.LineSegments).isLineSegments) lines.push(`${id}:${((o as THREE.LineSegments).geometry.getAttribute('position')?.count ?? 0) / 2}`);
    });
    this.canvas.dataset.objects = [...ids].sort().join(',');
    this.canvas.dataset.lines = lines.join(' ');
    const sig = this.litSignature();
    if (!this.lightsShown || sig !== this.litReady) {
      this.litReady = null;
      delete this.canvas.dataset.litReady;
      this.litWork = 0;
    }
    if (this.lightsOn) this.syncLights();
    else this.schedulePrewarm();
  }

  /** Compile the lit shaders in the background once the view has been idle a
   *  moment, so the first Lights on need not wait — only while the view runs. */
  private schedulePrewarm(): void {
    if (this.prewarm) clearTimeout(this.prewarm);
    this.prewarm = setTimeout(() => {
      this.prewarm = null;
      if (this.disposed || !this.raf || this.lightsOn || this.litWork) return;
      const sig = this.litSignature();
      if (!sig || this.litReady === sig) return;
      this.compileLit(this.litWork = ++this.litGen);
    }, PREWARM_IDLE_MS);
  }

  /** Compile every material's lit program — the lit look applied, compiled and
   *  taken back in this one task, so no frame shows it —, then wait for each to
   *  link: polled where the GPU links in parallel, else one forced per task, so
   *  the page stays responsive between them. */
  private compileLit(g: number): void {
    const sig = this.litSignature();
    const shown = this.lightsShown;
    this.lightsShown = true;
    this.applyLights();
    let mats: Set<THREE.Material>;
    try { mats = this.renderer.compile(this.scene, this.camera) as Set<THREE.Material>; }
    finally { this.lightsShown = shown; this.applyLights(); }
    type Program = THREE.WebGLProgram & { isReady?: () => boolean };
    const left = new Set<Program>();
    for (const m of mats) {
      const p = (this.renderer.properties.get(m) as { currentProgram?: Program }).currentProgram;
      if (p) left.add(p);
    }
    const parallel = this.renderer.extensions.get('KHR_parallel_shader_compile') != null;
    // A program a rebuild has let go of meanwhile (three.js deleted it: no
    // `program`) is done with — asking it would throw, waiting on it never end.
    const step = (): void => {
      if (this.disposed || this.litWork !== g) return;
      if (parallel) { for (const p of left) if (!p.program || (p.isReady?.() ?? true)) left.delete(p); }
      else { const p = left.values().next().value; if (p) { left.delete(p); if (p.program) p.getUniforms(); } }
      if (left.size) { setTimeout(step, parallel ? 10 : 0); return; }
      this.litWork = 0;
      this.litReady = sig;
      // Test seam: the light counts whose lit shaders are ready.
      this.canvas.dataset.litReady = sig;
      if (this.lightsOn) this.syncLights();
    };
    step();
  }

  private applyLights(): void {
    const on = this.lightsShown, look = on ? NIGHT : DAY;
    let shining = 0;
    this.content?.traverse((o) => {
      const role = nodeOf(o)?.role;
      if (role === 'viewRig') { o.visible = on; if (on) shining += o.children.filter((l) => (l as THREE.Light).isLight).length; } else if (role === 'fileRig') o.visible = false;
      if (role !== 'glow') return;
      const mats = (o as THREE.Mesh).material;
      for (const m of Array.isArray(mats) ? mats : mats ? [mats] : []) (m as THREE.MeshStandardMaterial).emissiveIntensity = on ? GLOW.on : GLOW.off;
    });
    this.hemi.intensity = look.hemi;
    this.ambient.intensity = look.ambient;
    this.dir.intensity = look.key;
    this.dir.color.set(look.keyColor);
    this.scene.environmentIntensity = look.env;
    (this.scene.background as THREE.Color).set(look.background);
    this.ground.material = on ? this.groundNight : this.groundDay;
    this.grid.visible = !on;
    // Test seam: how many lights shine.
    this.canvas.dataset.lights = String(shining);
  }

  /** Run a glTF export with the file's lights in the view's place — glTF holds
   *  spot and point lights, no area light —, whatever the view shows; restored
   *  once it is done. */
  private withFileRigs<T>(fn: () => Promise<T>): Promise<T> {
    this.content?.traverse((o) => { const role = nodeOf(o)?.role; if (role === 'fileRig') o.visible = true; else if (role === 'viewRig') o.visible = false; });
    return fn().finally(() => { this.applyLights(); this.dirty = true; });
  }

  /** Run an export with the ceilings shown: an export always includes them,
   *  whatever the view hides — restored once `fn` (and its promise) is done. */
  private withCeilingsShown<T>(fn: () => T): T {
    if (!this.ceilingsHidden) return fn();
    this.ceilingsHidden = false;
    this.applyCeilings();
    const restore = (): void => { this.ceilingsHidden = true; this.applyCeilings(); this.dirty = true; };
    let out: T;
    try { out = fn(); } catch (e) { restore(); throw e; }
    if (out instanceof Promise) return out.finally(restore) as unknown as T;
    restore();
    return out;
  }

  /** Export the current content as a binary glTF (.glb): its fixtures' glow, and
   *  the rooms' lights (the file's rigs). */
  exportGLB(): Promise<ArrayBuffer> {
    const target = this.content ?? new THREE.Group();
    return this.withCeilingsShown(() => this.withFileRigs(() => new Promise<ArrayBuffer>((resolve, reject) => {
      new GLTFExporter().parse(target, (out) => resolve(out as ArrayBuffer), (err) => reject(err), { binary: true });
    })));
  }

  /** Export the current content as a Wavefront OBJ + companion MTL (with colours). */
  exportOBJ(): { obj: string; mtl: string } {
    return this.withCeilingsShown(() => groupToObjMtl(this.content ?? new THREE.Group()));
  }

  /** Export the current content as a 3D DXF (R12 3DFACE mesh, Z-up). */
  exportDxf3d(): string {
    return this.withCeilingsShown(() => groupToDxf3d(this.content ?? new THREE.Group()));
  }

  /** Export the current content as a binary STL (geometry only, no colour). */
  exportSTL(): Uint8Array {
    return this.withCeilingsShown(() => {
      const out = new STLExporter().parse(this.content ?? new THREE.Group(), { binary: true }) as unknown as DataView;
      return new Uint8Array(out.buffer, out.byteOffset, out.byteLength);
    });
  }

  /** Export the current content as a USDZ package (materials + textures) for AR.
   *  The scene is authored in millimetres, but USDZ declares `metersPerUnit = 1`,
   *  so exporting raw makes a 2 m room 2000 m — far too large for iOS Quick Look to
   *  frame, which opens blank. Wrap a clone of the content in a group scaled by
   *  1/1000 (mm → m); `buildXform` bakes each mesh's `matrixWorld` (now including
   *  the scale) so the model exports at real-world metres. The clone leaves the
   *  live preview scene untouched. */
  exportUSDZ(): Promise<Uint8Array> {
    return this.withCeilingsShown(() => {
      const src = this.content ?? new THREE.Group();
      const wrap = new THREE.Group();
      wrap.scale.setScalar(0.001);
      wrap.add(src.clone(true));
      wrap.updateMatrixWorld(true);
      return new USDZExporter().parseAsync(wrap);
    });
  }

  dispose(): void {
    this.disposed = true;
    if (this.prewarm) clearTimeout(this.prewarm);
    this.stop();
    this.sizeWatch?.disconnect();
    this.sizeWatch = null;
    this.woodWatch();
    this.setSelection([]);
    this.hover = null;
    this.fitHover();
    if (this.content) { this.scene.remove(this.content); disposeGroup(this.content, this.xrayed); this.content = null; }
    this.freeRetired();
    this.setSectionPlanes([]);
    this.caps.clear();
    this.capMat.dispose();
    for (const m of Object.values(this.planeMats)) { m.fill.dispose(); m.line.dispose(); }
    this.edges.dispose();
    this.controls.dispose();
    this.renderer.dispose();
    // Release the GPU context promptly. Without this, disposed renderers keep
    // their WebGL context alive until GC, so repeated exports through throwaway
    // offscreen viewers can hit Chromium's context cap and drop older contexts.
    this.renderer.forceContextLoss();
  }
}

/** A section plane's translucent fill and its outline, drawn over everything. */
function planeFill(color: number): THREE.MeshBasicMaterial {
  return new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.14, side: THREE.DoubleSide, depthWrite: false, toneMapped: false });
}
function planeLine(color: number): THREE.LineBasicMaterial {
  return new THREE.LineBasicMaterial({ color, transparent: true, depthTest: false, toneMapped: false });
}

const closedCache = new WeakMap<THREE.BufferGeometry, boolean>();
/** Whether a solid's pieces, with its seat, close a surface together
 *  (`closedSurface`): what the cut's fill may cover. Cached on the first piece's
 *  geometry — a solid's pieces are made together and never regrouped. */
function closedSolid(pieces: THREE.Mesh[], seat: THREE.BufferGeometry | null): boolean {
  const g0 = pieces[0].geometry;
  let closed = closedCache.get(g0);
  if (closed === undefined) {
    const geos = seat ? [...pieces.map((m) => m.geometry), seat] : pieces.map((m) => m.geometry);
    closed = closedSurface(geos.map((g) => ({ pos: positionsOf(g), index: g.getIndex()?.array ?? null })));
    closedCache.set(g0, closed);
  }
  return closed;
}
/** A geometry's corner positions, flat (an interleaved attribute copied out). */
function positionsOf(g: THREE.BufferGeometry): ArrayLike<number> {
  const pos = g.getAttribute('position');
  if (!pos) return [];
  if (!(pos as THREE.InterleavedBufferAttribute).isInterleavedBufferAttribute && pos.itemSize === 3) return pos.array;
  const out = new Float32Array(pos.count * 3);
  for (let i = 0; i < pos.count; i++) { out[3 * i] = pos.getX(i); out[3 * i + 1] = pos.getY(i); out[3 * i + 2] = pos.getZ(i); }
  return out;
}
/** What a mesh or a line shows (its record): its object, and its feature when
 *  it is one; null for none (an underlay). */
function pickOf(o: THREE.Object3D): Pick3d | null {
  const n = nodeOf(o);
  if (!n?.owner) return null;
  return n.part ? { id: n.owner, part: n.part } : { id: n.owner };
}

/** How near (px) a click takes a 1 px line. */
const LINE_PICK_PX = 5;

/** A point of three's frame (x, height, −plan y) in the drawing's. */
const toDrawing = (v: THREE.Vector3): Pt3 => ({ x: v.x, y: -v.z, z: v.y });

/** Whether a node shows: it and every group it is in visible. */
function shown(o: THREE.Object3D | null): boolean {
  for (let n = o; n; n = n.parent) if (!n.visible) return false;
  return true;
}


/** Free each distinct geometry/material of a group once (a solid's seat, and
 *  X-ray's own under its copy, `xrayed`, too). Textures are cached and shared
 *  across rebuilds (see scene3d), so they are intentionally not disposed. */
function disposeGroup(group: THREE.Object3D, xrayed: WeakMap<THREE.Object3D, THREE.Material | THREE.Material[]>): void {
  const geos = new Set<THREE.BufferGeometry>();
  const mats = new Set<THREE.Material>();
  group.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (mesh.geometry && !geos.has(mesh.geometry)) { geos.add(mesh.geometry); mesh.geometry.dispose(); }
    nodeOf(o)?.solid?.seat?.dispose();
    const list = Array.isArray(mesh.material) ? mesh.material : mesh.material ? [mesh.material] : [];
    const orig = xrayed.get(o);
    xrayed.delete(o);
    for (const m of [...list, ...(orig ? (Array.isArray(orig) ? orig : [orig]) : [])]) if (m && !mats.has(m)) { mats.add(m); m.dispose(); }
  });
}
